import { api } from './client';
import type { Conversation, Message, PaginatedResponse, WorkPlan } from '@/types';

export interface CreateConversationRequest {
  projectId: string;
  title?: string;
  aiProvider?: string;
  aiModel?: string;
}

export interface StartChatRequest {
  projectId: string;
  message: string;
  title?: string;
  autoApprove?: boolean;
  attachments?: string[];
}

export interface SendMessageRequest {
  conversationId: string;
  message: string;
  autoApprove?: boolean;
  attachments?: string[];
}

export const conversationsApi = {
  list: (projectId: string, page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<Conversation>>(
      `/api/conversations?projectId=${projectId}&page=${page}&pageSize=${pageSize}`
    ),

  get: (id: string) =>
    api.get<Conversation & { messages: Message[] }>(`/api/conversations/${id}`),

  create: (data: CreateConversationRequest) =>
    api.post<Conversation>('/api/conversations', data),

  update: (id: string, data: { title?: string; status?: string }) =>
    api.put<Conversation>(`/api/conversations/${id}`, data),

  archive: (id: string) =>
    api.post(`/api/conversations/${id}/archive`),

  delete: (id: string) =>
    api.delete(`/api/conversations/${id}`),

  getMessages: (conversationId: string, limit = 50, before?: string) => {
    let url = `/api/conversations/${conversationId}/messages?limit=${limit}`;
    if (before) {
      url += `&before=${before}`;
    }
    return api.get<Message[]>(url);
  },

  // Work plans
  getWorkPlans: (conversationId: string) =>
    api.get<WorkPlan[]>(`/api/workplans?conversationId=${conversationId}`),

  getWorkPlan: (id: string) =>
    api.get<WorkPlan>(`/api/workplans/${id}`),

  approveWorkPlan: (id: string) =>
    api.post(`/api/workplans/${id}/approve`),

  rejectWorkPlan: (id: string, reason: string) =>
    api.post(`/api/workplans/${id}/reject`, { reason }),

  cancelWorkPlan: (id: string, reason?: string) =>
    api.post(`/api/workplans/${id}/cancel`, { reason }),

  retryWorkPlan: (id: string, fromPhaseId?: string) =>
    api.post(`/api/workplans/${id}/retry`, { fromPhaseId }),

  // Phase actions
  approvePhase: (phaseId: string) =>
    api.post(`/api/workplans/phases/${phaseId}/approve`),

  rejectPhase: (phaseId: string, reason: string) =>
    api.post(`/api/workplans/phases/${phaseId}/reject`, { reason }),
};

// Chat streaming is handled via api.stream() directly
export const chatApi = {
  startChat: (data: StartChatRequest) =>
    api.stream('/api/chat/start', data),

  sendMessage: (data: SendMessageRequest) =>
    api.stream('/api/chat/message', data),
};
